import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './BottomNavigation.css';

interface BottomNavigationProps {
  onNavClick: (section: string) => void;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ onNavClick }) => {
  const { isAuthenticated, hasRole } = useAuth();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll detection to hide/show navigation
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Only hide if scrolling down and moved more than 10px
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsVisible(false);
    } else if (currentScrollY < lastScrollY) {
      setIsVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    // Throttle scroll events for better performance
    let timeoutId: NodeJS.Timeout;
    const throttledScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 10);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  // Function to check if user can access a feature
  const canAccess = (feature: string) => {
    switch (feature) {
      case 'bonds':
        return true; // Allow public access
      case 'report-sources':
      case 'content-search':
        return isAuthenticated;
      case 'web-search':
      case 'search-history':
      case 'search-results':
        return isAuthenticated && (hasRole('ADMIN') || hasRole('USER'));
      case 'user-management':
      case 'reports':
      case 'experiments':
      case 'feedback-management':
        return isAuthenticated && hasRole('ADMIN');
      case 'feedback':
        return isAuthenticated;
      default:
        return false;
    }
  };

  // Main navigation items for mobile
  const getMainNavItems = () => {
    const items = [];

    // Home/Dashboard
    items.push({
      to: "/",
      label: hasRole('ADMIN') ? 'Dashboard' : t('navigation.home'),
      icon: hasRole('ADMIN') ? 'dashboard' : 'home',
      key: 'home'
    });

    // Bonds (always visible)
    if (canAccess('bonds')) {
      items.push({
        to: "/bonds",
        label: isAuthenticated ? t('navigation.bonds') : t('navigation.exampleData'),
        icon: 'account_balance',
        key: 'bonds'
      });
    }

    // Content Search for authenticated users
    if (canAccess('content-search')) {
      items.push({
        to: "/content-search",
        label: t('navigation.contentSearch'),
        icon: 'search',
        key: 'content-search'
      });
    }

    // Web Search for PRO users
    if (canAccess('web-search')) {
      items.push({
        to: "/web-search",
        label: t('navigation.webSearch'),
        icon: 'public',
        key: 'web-search'
      });
    }

    // Report Sources for authenticated users
    if (canAccess('report-sources')) {
      items.push({
        to: "/report-sources",
        label: t('navigation.reportSources'),
        icon: 'description',
        key: 'report-sources'
      });
    }

    // Feedback for authenticated users
    if (canAccess('feedback')) {
      items.push({
        to: "/feedback",
        label: 'Feedback',
        icon: 'feedback',
        key: 'feedback'
      });
    }

    // Limit to 5 items for mobile
    return items.slice(0, 5);
  };

  const navItems = getMainNavItems();

  return (
    <nav className={`bottom-navigation ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="bottom-nav-container">
        {navItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) =>
              `bottom-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => onNavClick(item.key)}
          >
            <span className="material-icons bottom-nav-icon">
              {item.icon}
            </span>
            <span className="bottom-nav-label">
              {item.label}
            </span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export default BottomNavigation;